export function useWadError() {
  const { $bugsnag } = useNuxtApp();

  const getErrorMessageTokens = (errorMessage: string | null): ErrorMessageToken[] | null => {
    if(!errorMessage) {
      return null;
    }
    const tokens: ErrorMessageToken[] = []
    const links = errorMessage.match(/<<.+?>>/g)

    if (links && links.length) {
      let message = errorMessage
      for (let i = 0; i < links.length; i++) {
        const currentLink = links[i];
        if (currentLink) {
            const linkPosition = message.indexOf(currentLink)
            if (linkPosition > 0) {
             tokens.push({ text: message.substring(0, linkPosition), isLink: false })
            }
            tokens.push({ text: currentLink.replace(/<<|>>/mg, ''), isLink: true })
            message = message.substring(linkPosition + currentLink.length)
        }
      }
      if (message) {
        tokens.push({ text: message, isLink: false })
      }
    } else {
      tokens.push({ text: errorMessage, isLink: false })
    }

    return tokens
  }

  const wadGenericError = 'Sorry, something just went wrong. Please try again in a few moments. If you continue to get this error please contact support.';

  // Same approach as wad-platform's useWadError.ts. The list mirrors
  // ErrorCodeEnum::isDisplayableMessage() in wad-api and the copy in ServerErrorStore, so keep the
  // four in step. Any code not listed is withheld, since most API messages are written for
  // developers rather than users.
  const displayableErrorCodes = [
    'BAD_DATA',
    'TOO_MANY_ATTEMPTS',
    'MFA_TOKEN_EXPIRED',
    'MFA_NOT_ACTIVE',
    'ACCOUNT_SUSPENDED',
    'ACCOUNT_MOVED',
    'SIGNUP_MOVED',
  ];

  const parseErrorFromException = (exception: any, handleValidation: boolean = true, customMessage: string|null = null) => {
    // The API wraps most error bodies one level down, so the fields sit at exception.data.data.
    // Only the resources setting $wrap = null are flat. Descends only when the inner object looks
    // like an error body, the same guard ServerErrorStore uses, so a flat payload carrying its own
    // `data` key is left alone.
    const outer = exception.data;
    const nested = outer && typeof outer === 'object' ? outer.data : null;
    const looksLikeError = nested && typeof nested === 'object'
      && ('error_code' in nested || 'message' in nested || 'success' in nested);
    const payload = (looksLikeError ? nested : outer) || {};

    // Show the server's sentence only when it was written to be read by a user, so a 5xx or an
    // internal message cannot reach a screen. Matched on the code, never on the message text.
    const errorCode = payload.error_code || payload.error?.code || null;
    const msg = errorCode && displayableErrorCodes.includes(errorCode)
      ? (payload.message || payload.error?.message || null)
      : null;
    const errorData = <ErrorParsed>{
      fields: {},
      message: customMessage || (msg || wadGenericError),
      type: 'error',
      status: exception.status || null
    }
    if(errorData.status === 422 && handleValidation) {
      let responseData = exception.data || {};
      if(responseData.data) {
        responseData = responseData.data
      }
      const errors = responseData.errors ? responseData.errors : responseData;
      for (const [key, value] of Object.entries(errors)) {
        errorData.fields[key] = Array.isArray(value) ? value[0] : value;
      }
      if(Object.keys(errorData.fields).length){
        errorData.message = null;
      }
    } else if (isConnectivityError(exception)) {
      // A failure with no HTTP response behind it is a connectivity problem whatever
      // navigator.onLine happens to say (it has been seen stuck false while the network was
      // fine, and it stays true on dead conference wifi). Shares its definition with the error
      // page and the lead-scan fallback.
      errorData.message = 'It seems that you are offline. Please check your internet connection and try again.';
    }
    // A 4xx is the server deliberately answering the client with a sentence written for the person
    // reading it, such as a wrong password or an expired code. Those are not faults, and reporting
    // them would open an event on every failed login. Real breakage still reports: 5xx, the custom
    // 550, and statusless network failures.
    const status = errorData.status;
    const isClientError = typeof status === 'number' && status >= 400 && status < 500;
    if(!isClientError) {
      $bugsnag?.notify(exception);
    }
    return errorData;
  }

  return {
    getErrorMessageTokens,
    parseErrorFromException,
    // Exported so ServerErrorStore can fall back to the same sentence rather than duplicating it.
    wadGenericError
  }
}
